<svelte:options customElement="dst-notifications-wrapper" />

<script lang="ts">
	import type { AuthSession, UserData } from '@/global.d.ts';

	import * as Auth from '@/lib/authentication.ts';
	import { register as registerClosables } from '@/lib/closable.ts';
	import { Logger } from '@/lib/log.ts';
	import { trackUserAction } from '@/lib/tracking.ts';
	import { removeNewNotificationsIndicator } from '@/lib/utils.ts';
	// Svelte stores
	import notifications from '@/stores/notification_store.ts';
	import * as userStore from '@/stores/userState.ts';
	import { dstBellBadgeColor, dstHeaderBell, dstHeaderBellBadge } from '@derstandard/styling-toolkit/src/svg/lib.js';
	import { pushToDataLayer } from '@derstandard/tracking';
	import { createMutationObserver } from '@derstandard/utils';
	import { mdiBell } from '@mdi/js';
	import { afterUpdate, onMount } from 'svelte';

	export let hasuseractions;
	export let isapp = false;

	const log = Logger('header.DstNotificationsWrapper');

	let userState: UserData;
	let root: HTMLElement;
	let notificationsFlyout: HTMLElement;

	$: {
		if ((root && $notifications?.hasNewNotifications) || (root && isapp)) {
			(root.getRootNode() as ShadowRoot).host.classList.add('notifications-active');
		} else if (root) {
			(root.getRootNode() as ShadowRoot).host.classList.remove('notifications-active');
		}
	}
	$: ariaExpanded = false;

	/**
	 * Refresh session data
	 */
	async function getSession(): Promise<AuthSession> {
		const session = await Auth.getSession();

		if (session) {
			log.debug('Updating userStore from session', session);
			userStore.set(await userStore.get());
		}

		return session;
	}

	const { disconnect, observe } = createMutationObserver({
		onChanged: (mutation) => {
			const target = mutation.target as Element;

			if (target.getAttribute('id') !== 'app-notifications') {
				return;
			}

			const attrName = mutation.attributeName;
			const newValue = target.getAttribute(attrName);

			ariaExpanded = newValue === 'true';
		},
	});

	onMount(async () => {
		log.debug('onMount');

		// While I don't understand the need for two global values for the same thing, it should be noted that this
		// value is used in multiple places in the clientside repository
		window.DERSTANDARD.notificationsEnabled =
			typeof window?.DERSTANDARD?.pageConfig?.settings?.disableNotifications !== 'undefined'
				? !window.DERSTANDARD.pageConfig.settings.disableNotifications
				: true;

		const session = await getSession();

		if (session) {
			// notifications only exist for logged in users

			// @ts-expect-error Uses Vite resolve.alias
			await import('__NOTIFICATIONS_NEXT_URL__');
			void (await import('../DstCounter/DstCounter.webcomponent.svelte')).default;
		}
	});

	log.debug('subscribing to userStore');
	userStore.store.subscribe((value) => {
		if (value) {
			log.debug('userstate changed. updating local userstate.');
			userState = value;
		} else {
			log.debug("userstate not available. setting local userstate to 'not logged in'");
			userState = {
				isLoggedIn: false,
			};
		}
	});

	afterUpdate(() => registerClosables(root));

	Auth.onSignedIn(async () => {
		userStore.set(await userStore.get());
	});

	/**
	 *
	 * @param node
	 */
	function mutation(node: HTMLElement) {
		observe(node);

		return {
			destroy() {
				disconnect();
			},
		};
	}

	/**
	 * calls getNotification on opening the flyout
	 * @returns
	 */
	function toggleIsOpen() {
		const isOpen = notificationsFlyout.dataset?.closableActive !== 'true';

		if (isOpen) {
			trackUserAction($notifications.hasNewNotifications ? 'header_shownotif_new' : 'header_shownotif');
			pushToDataLayer({ event: 'notifications.show' });
			removeNewNotificationsIndicator();
		}
	}

	/**
	 * Click handler for the notifications tab change
	 */
	function clickNotificationsTab() {
		trackUserAction('header_tabchange_profile');
		removeNewNotificationsIndicator();
	}
</script>

{#if window.DERSTANDARD.notificationsEnabled && userState?.isLoggedIn}
	<div class="dst-notifications" bind:this={root} use:mutation>
		<button
			aria-controls="app-notifications"
			aria-expanded={ariaExpanded}
			title="Mitteilungen"
			data-closable-target="app-notifications"
			on:click={toggleIsOpen}
			type="button"
		>
			<span> Mitteilungen</span>

			{#if $notifications.hasNewNotifications}
				{#if isapp}
					<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
						{@html dstBellBadgeColor}
					</svg>
				{:else}
					<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50">
						{@html dstHeaderBellBadge}
					</svg>
				{/if}
			{:else if isapp}
				<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
					<path fill="currentColor" d={mdiBell} />
				</svg>
			{:else}
				<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50">
					{@html dstHeaderBell}
				</svg>
			{/if}
		</button>

		<dst-flyout
			id="app-notifications"
			data-closable="app-notifications"
			data-closable-active="false"
			bind:this={notificationsFlyout}
		>
			{#if hasuseractions}
				<div class="flyout--tabs" slot="tabs">
					<button
						data-closable-target="app-useractions"
						data-closable-close="true"
						on:click={clickNotificationsTab}
						type="button"
					>
						Profil
					</button>

					<button type="button" disabled>
						Mitteilungen

						<dst-counter />
					</button>
				</div>
			{/if}
			<dst-notifications />
		</dst-flyout>
	</div>
{/if}

<style lang="scss" global>
	@use './DstNotificationsWrapper.scss';
</style>
